import type { ItemId } from '../inventory';
import type { Asteroid } from './Asteroid';

interface SpaceDropEntry {
  readonly itemId: ItemId;
  readonly weight: number;
  readonly yieldMin: number;
  readonly yieldMax: number;
}

/** Drop table for "standard" asteroids — the kind that previously dropped
 *  nothing. Tilted heavily toward common quartz with rarer crystal pulls
 *  so destroying belts feels worthwhile without flooding the economy. */
const STANDARD_DROPS: readonly SpaceDropEntry[] = [
  { itemId: 'raw-quartz', weight: 8, yieldMin: 1, yieldMax: 2 },
  { itemId: 'ion-ice', weight: 3, yieldMin: 1, yieldMax: 2 },
  { itemId: 'stellar-iron', weight: 3, yieldMin: 1, yieldMax: 2 },
  { itemId: 'plasma-crystal', weight: 1, yieldMin: 1, yieldMax: 1 }
];

export interface ItemDrop {
  readonly itemId: ItemId;
  readonly count: number;
}

/** Roll the loot for a destroyed standard asteroid. Larger rocks lean
 *  toward the upper end of the yield range — a 12u boulder is worth more
 *  than a 2u pebble. */
export function rollAsteroidStandardDrop(
  size: number,
  rng: () => number = Math.random
): ItemDrop {
  let total = 0;
  for (const entry of STANDARD_DROPS) total += entry.weight;
  let roll = rng() * total;
  let chosen: SpaceDropEntry = STANDARD_DROPS[0]!;
  for (const entry of STANDARD_DROPS) {
    roll -= entry.weight;
    if (roll <= 0) {
      chosen = entry;
      break;
    }
  }
  const sizeBonus = Math.floor(size / 5);
  const range = chosen.yieldMax - chosen.yieldMin;
  const base = chosen.yieldMin + Math.floor(rng() * (range + 1));
  return { itemId: chosen.itemId, count: base + sizeBonus };
}

/** Compute every drop produced by destroying an asteroid — kind-aware so
 *  every visible asteroid type maps to the right item. The `standard`
 *  fallback exists only for save-game / legacy compatibility; current
 *  spawn rolls always pick a typed kind. */
export function rollAsteroidLoot(asteroid: Asteroid): readonly ItemDrop[] {
  switch (asteroid.kind) {
    case 'gold':
      return [{ itemId: 'gold-ore', count: asteroid.goldYield }];
    case 'uranium':
      return [{ itemId: 'uranium-ore', count: asteroid.uraniumYield }];
    case 'iron':
      return [{ itemId: 'stellar-iron', count: asteroid.oreYield }];
    case 'quartz':
      return [{ itemId: 'raw-quartz', count: asteroid.oreYield }];
    case 'ice':
      return [{ itemId: 'ion-ice', count: asteroid.oreYield }];
    case 'plasma':
      return [{ itemId: 'plasma-crystal', count: asteroid.oreYield }];
    case 'standard':
    default:
      return [rollAsteroidStandardDrop(asteroid.size)];
  }
}
